import { Injectable, NgZone } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  filter,
  EMPTY,
  firstValueFrom,
  BehaviorSubject,
  map,
  pairwise,
  distinctUntilChanged,
  Observable,
  combineLatest,
  skip,
  Subscription,
} from 'rxjs';
import * as moment from 'moment';
import { filterNil } from '@ngneat/elf';
import { EndEventBlockingModalComponent } from '../sessions/session/dialogs/end-event-blocking-modal/end-event-blocking-modal.component';
import { InspectModeRepository } from '../state/inspect-mode-repository.service';
import { ISpaceUI, SpaceRepository } from '../state/space.repository';
import { RealtimeToken } from '../models/messaging';
import { EventWithTimingInfo } from '../sessions/session/attendance/attendance-ui-models';
import { IconBackground } from '../ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import { INFOS } from '../common/utils/notification-constants';
import { Event, EVENT_STATUS, EventState } from '../models/event';
import { RealtimeDataUpdate } from '../models/realtime';
import { ISession, RealTimeUpdatesDataTypes, Session } from '../models/session';
import { AttendanceManagerService } from './attendance-manager.service';
import { EventService } from './event.service';
import { RealtimeService } from './realtime.service';
import { SessionSharedDataService } from './session-shared-data.service';
import { SpaceNavigationService } from './space-navigation.service';
import { ModalManagerService } from './modal-manager.service';
import { WbDialogService } from './wb-dialog.service';
import { UserService } from './user.service';
import {
  NotificationToasterService,
  NotificationIconConfig,
  NotificationIconTypes,
  V2NotificationDataBuilder,
  NotificationType,
} from './notification-toaster.service';
import { TelemetryService } from './telemetry.service';
import { RtcServiceController, StopVideoCallReason } from './rtc.service';
import { SpaceRecordingService } from './space-recording.service';
import { AudioChimes, AudioService } from './audio.service';
import { PresenceProvider } from './presence-provider';
import { EndEventRedirectManagerService } from './end-event-redirect-manager.service';
import { NetworkStatus, SessionsSyncService } from './sessions-sync.service';
import { ObserverModeService } from './observer-mode.service';

export interface EventTimeLeft {
  formattedTime: string;
  timeLeftSeconds: number;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class EventStateManagerService {
  private readonly TELEMETRY_SOURCE = 'event-state-manager-monitoring';
  private readonly CRITICAL_TIME_THRESHOLD_SECONDS = 60 * 5; // 5 minutes
  private readonly MODAL_TIME_THRESHOLD_SECONDS = 60; // 1 minute
  private eventEndingSoonNotificationTimer?: ReturnType<typeof setTimeout>;
  private endEventModalTimer?: ReturnType<typeof setTimeout>;
  private extendEventSubscription?: Subscription;

  /*
    This map is used to get the localized label for the event extension time.
    The key is the number of seconds to extend the event by.
    The value is the localized label for the extension time.
  */
  public readonly EVENT_EXTENSION_TIME_TO_LOCALIZED_LABEL_MAP = new Map<number, string>([
    [5, this.translateService.instant('Extend by 5 mins')],
    [15, this.translateService.instant('Extend by 15 mins')],
  ]);

  // Private Subjects
  private readonly _timeLeft$ = new BehaviorSubject<EventTimeLeft | undefined>(undefined);

  // Public Observables
  public readonly timeLeft$ = this._timeLeft$.asObservable();
  public readonly isInCriticalTime$ = this.timeLeft$.pipe(
    map(
      (timeLeft: EventTimeLeft | undefined) =>
        timeLeft?.timeLeftSeconds &&
        timeLeft.timeLeftSeconds <= this.CRITICAL_TIME_THRESHOLD_SECONDS,
    ),
    distinctUntilChanged(),
  );

  // State Management
  private _activeEvent?: EventWithTimingInfo;
  private eventTimerInterval?: number;

  get activeEvent(): EventWithTimingInfo | undefined {
    return this._activeEvent;
  }

  /**
   * Tracks the ID of the last space that was subscribed to realtime updates.
   * This is necessary to prevent attempting to unsubscribe from spaces that were never subscribed to.
   */
  private lastSubscribedSpaceId: string | undefined;

  constructor(
    private attendanceManagerService: AttendanceManagerService,
    private modalManagerService: ModalManagerService,
    private wbDialogService: WbDialogService,
    private userService: UserService,
    private eventService: EventService,
    private spaceRepo: SpaceRepository,
    private translateService: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private sessionSharedDataService: SessionSharedDataService,
    private spaceNavigationService: SpaceNavigationService,
    private realtimeService: RealtimeService,
    private telemetryService: TelemetryService,
    private rtcServiceController: RtcServiceController,
    private spaceRecordingService: SpaceRecordingService,
    private audioService: AudioService,
    private presenceProvider: PresenceProvider,
    private endEventRedirectManagerService: EndEventRedirectManagerService,
    private ngZone: NgZone,
    private sessionsSyncService: SessionsSyncService,
    private inspectModeRepository: InspectModeRepository,
    private observerModeService: ObserverModeService,
  ) {
    this.init();
  }

  // --- / Core Initialization and Public Methods / ---
  /**
   * Initializes the event state manager by subscribing to the event ended observable and setting up the realtime update trigger.
   */
  public init(): void {
    this.setupEventEndedSubscription();
    this.setupRealtimeUpdateTrigger();
    this.setupCleanupRealtimeSubscription();
    this.handleDisconnections();
  }

  /**
   * Marks the current event as ended if it exists.
   */
  public async markCurrentEventAsEnded(): Promise<void> {
    if (this._activeEvent) {
      await this.markEventAsEnded(this._activeEvent._id);
    }
  }

  /**
   * Makes an API call to mark the event as ended in the backend.
   */
  public async markEventAsEnded(eventId: string): Promise<void> {
    try {
      await firstValueFrom(this.eventService.endEvent(eventId));
      this.telemetryService.logObject(
        { message: `Event with eventId=${eventId} is marked as ended` },
        this.TELEMETRY_SOURCE,
      );
    } catch (error) {
      this.telemetryService.logObject({ error }, this.TELEMETRY_SOURCE, 'error');
    }
  }

  private setupEventEndedSubscription(): void {
    this.attendanceManagerService.eventEnded$.pipe(distinctUntilChanged()).subscribe((eventId) => {
      if (
        this.spaceRepo.activeSpace &&
        this.isPostEventActionEnabledForSpace(this.spaceRepo.activeSpace) &&
        this._activeEvent?.state?.status !== EVENT_STATUS.COMPLETED
      ) {
        this.markEventAsEnded(eventId);
      }
    });
  }

  /**
   * Subscribes to the onGoingEvent$ observable to trigger the handleOngoingEvent method when an event is ongoing.
   * Skipped entirely in inspect mode (recording playback) to prevent modals, notifications, and redirects.
   */
  private setupRealtimeUpdateTrigger(): void {
    this.attendanceManagerService.onGoingEvent$
      .pipe(distinctUntilChanged(), filterNil())
      .subscribe((event: EventWithTimingInfo) => {
        // Skip entire event end flow in inspect mode (recording playback)
        if (this.inspectModeRepository.isInspectMode) {
          return;
        }

        // event started
        this._activeEvent = event;
        const space = this.spaceRepo.activeSpace;
        if (
          !space ||
          !this.isPostEventActionEnabledForSpace(space) ||
          event.state?.status === EVENT_STATUS.COMPLETED
        ) {
          return;
        }

        this.handleOngoingEvent();
        this.startEventTimer();
      });
  }

  /**
   * Cleans up the realtime subscription when the user leaves the space.
   */
  private setupCleanupRealtimeSubscription(): void {
    this.spaceNavigationService.leavingToHome$.subscribe(() => {
      if (this.lastSubscribedSpaceId) {
        this.realtimeService.unsubscribeSession(this.lastSubscribedSpaceId);
      }
    });
  }

  private handleDisconnections(): void {
    // Create a combined observable that emits when:
    // 1. Network status changes from DISCONNECTED to CONNECTED
    // 2. AND the realtime service has reconnected and re-established subscriptions
    combineLatest([
      this.sessionsSyncService.networkStatusObservable$.pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        pairwise(),
        filter(
          ([prev, current]) =>
            prev === NetworkStatus.DISCONNECTED && current === NetworkStatus.CONNECTED,
        ),
        map(() => true),
      ),
      // Listen for the realtime reconnection events
      this.realtimeService.realtimeInitOrReconnect$.pipe(
        untilDestroyed(this),
        // Skip the initial connection event
        skip(1),
      ),
    ])
      .pipe(
        // We're only interested when both conditions are true
        filter(
          ([networkReconnected, realtimeReconnected]) =>
            !!networkReconnected && !!realtimeReconnected,
        ),
        // Only if we have an active event
        filter(() => !!this._activeEvent),
      )
      .subscribe(() => {
        // Refetch events to get the latest state and update the event end time if it was extended
        this.attendanceManagerService.refetchEvents();

        if (!this._activeEvent) {
          return;
        }
        const eventEndTime = this._activeEvent?.end_time;
        const eventStateObservable: Observable<EventState> = this.eventService.getEventState(
          this._activeEvent._id,
        );
        eventStateObservable.subscribe((state: EventState) => {
          if (state.status === EVENT_STATUS.COMPLETED && this.activeEvent) {
            this.handleEventEndedRealtimeUpdate(this.activeEvent);
          } else if (eventEndTime && moment(eventEndTime).isBefore(moment()) && this.activeEvent) {
            // EVENT_STATUS is not completed but the event time passed
            // this is a fallback to mark the event as ended in this case
            // this can happen if there is only one user in the space and they get disconnected when the event ends
            // or when there are multiple users but they all get disconnected when the event ends
            this.markEventAsEnded(this.activeEvent._id);
          }
        });
      });
  }

  // --- / Event Handling Methods / ---
  /**
   * Handles the ongoing event by subscribing to the event end realtime updates.
   */
  private handleOngoingEvent(): void {
    const space = this.spaceRepo.activeSpace;
    if (!space) {
      return;
    }

    const realtimeToken = this.sessionSharedDataService.sessionAuthData[space._id]?.realtimeToken;
    if (!realtimeToken) {
      this.telemetryService.logObject(
        { spaceId: space._id, error: 'No realtime token' },
        this.TELEMETRY_SOURCE,
        'error',
      );
      return;
    }

    this.lastSubscribedSpaceId = space._id;
    this.subscribeToEventEndRealtimeUpdate(space._id, realtimeToken);
    this.subscribeToExtendEventRealtimeUpdate(space._id, realtimeToken);
  }

  private subscribeToExtendEventRealtimeUpdate(
    spaceId: string,
    realtimeToken: RealtimeToken,
  ): void {
    if (this.extendEventSubscription) {
      this.extendEventSubscription.unsubscribe();
    }

    this.extendEventSubscription = this.realtimeService
      .subscribeSession(spaceId, realtimeToken)
      .pipe(
        filter(
          (update: RealtimeDataUpdate) =>
            update?.dataType === RealTimeUpdatesDataTypes.EXTEND_EVENT,
        ),
        catchError((error) => {
          this.telemetryService.logObject({ error }, this.TELEMETRY_SOURCE, 'error');
          return EMPTY;
        }),
      )
      .subscribe((update: RealtimeDataUpdate) => {
        this.telemetryService.logObject({ realtimeUpdate: update }, this.TELEMETRY_SOURCE);

        const newEndTime = update.data?.end_time;

        if (newEndTime) {
          if (this._activeEvent) {
            this._activeEvent = {
              ...this._activeEvent,
              end_time: newEndTime,
            };
          }

          this.cleanupTimers();
          this.startEventTimer();
          this.attendanceManagerService.refetchEvents();

          // Show notification about extended time
          this.showEventExtendedNotification();
        }
      });
  }

  /**
   * Handles the realtime update received when an event ends by stopping the call and redirecting.
   */
  private async handleEventEndedRealtimeUpdate(event: Event): Promise<void> {
    // Update _activeEvent while preserving the timing info properties
    this._activeEvent = {
      ...this._activeEvent,
      ...event,
      eventStartTimeOffsetMillis: this._activeEvent?.eventStartTimeOffsetMillis ?? 0,
      eventEndtTimeOffsetMillis: this._activeEvent?.eventEndtTimeOffsetMillis ?? 0,
      startTimeOnClient: this._activeEvent?.startTimeOnClient,
    } as EventWithTimingInfo;
    try {
      await this.endCall();
    } catch (error) {
      this.telemetryService.logObject({ error }, this.TELEMETRY_SOURCE, 'error');
    }

    const eventId = this._activeEvent?._id;

    await this.endEventRedirectManagerService.redirectToPostEventUrl(
      this.spaceRepo.activeSpace,
      eventId,
    );
  }

  // --- / Realtime Communication Methods / ---
  /**
   * Subscribes to the event end realtime update, this observable is triggered when the event is marked as completed by any of the attendees.
   */
  private subscribeToEventEndRealtimeUpdate(spaceId: string, realtimeToken: RealtimeToken): void {
    this.realtimeService
      .subscribeSession(spaceId, realtimeToken)
      .pipe(
        filter((update) => update?.dataType === RealTimeUpdatesDataTypes.END_EVENT),
        catchError((error) => {
          this.telemetryService.logObject({ error }, this.TELEMETRY_SOURCE, 'error');
          return EMPTY;
        }),
      )
      .subscribe(async (update: RealtimeDataUpdate) => {
        this.telemetryService.logObject({ realtimeUpdate: update }, this.TELEMETRY_SOURCE);
        await this.handleEventEndedRealtimeUpdate(update.data.event);
      });
  }

  // --- / Event End Actions / ---
  /**
   * Ends the call if the user is present in the call.
   */
  private async endCall(): Promise<void> {
    if (
      !(await firstValueFrom(
        this.presenceProvider.isUserPresentInCall$(this.userService.user?.value?.user?._id ?? ''),
      ))
    ) {
      return;
    }
    await this.rtcServiceController.stopVideoCall(
      this.spaceRecordingService,
      StopVideoCallReason.END_CALL_EVENT_ENDED,
    );
    await this.audioService.playAudio(AudioChimes.youLeaveCall);
  }

  /**
   * Opens the EndEventBlockingModal if it's not already open.
   */
  private openEndEventModal(eventId: string): void {
    if (this.observerModeService.isObserverModeActive()) {
      return;
    }
    if (this.wbDialogService.getDialogRefOf(EndEventBlockingModalComponent)) {
      // Modal is already open
      return;
    }

    this.modalManagerService.showModal(
      EndEventBlockingModalComponent,
      {
        disableClose: true,
        panelClass: 'end-event-blocking-dialog-container',
        maxWidth: '380px',
      },
      {
        afterClosed: async (confirmed: boolean) => {
          if (confirmed) {
            await this.markEventAsEnded(eventId);
          }
        },
      },
    );
  }

  /**
   * Starts the timer that updates timeLeft$ with the remaining time for the current event
   */
  private startEventTimer(): void {
    // Clear any existing timer
    if (this.eventTimerInterval) {
      cancelAnimationFrame(this.eventTimerInterval);
    }

    // Initial update
    this.updateTimeLeft();
    this.setupEventTimers();

    // Run timer outside Angular's zone for better performance
    this.ngZone.runOutsideAngular(() => {
      let previousTime = performance.now();
      const targetInterval = 1000 / 60; // 60 FPS

      const updateTimer = () => {
        const currentTime = performance.now();
        const deltaTime = currentTime - previousTime;

        if (deltaTime >= targetInterval) {
          previousTime = currentTime;

          // Run the update inside Angular zone only when we need to update the UI
          this.ngZone.run(() => {
            this.updateTimeLeft();
          });
        }

        this.eventTimerInterval = requestAnimationFrame(updateTimer);
      };

      this.eventTimerInterval = requestAnimationFrame(updateTimer);
    });
  }

  /**
   * Sets up timers for:
   * - Event ending soon notification (5 minutes before the event ends)
   * - Event ending modal (1 minute before the event ends)
   */
  private setupEventTimers(): void {
    if (!this._activeEvent?.end_time) {
      return;
    }

    // Clear existing timers
    this.cleanupTimers();

    const endMoment = moment(this._activeEvent.end_time);
    const timeUntilEnd = endMoment.diff(moment(), 'milliseconds');

    // Set notification timer (5 minutes before)
    const timeUntilNotification = timeUntilEnd - this.CRITICAL_TIME_THRESHOLD_SECONDS * 1000;
    if (timeUntilNotification > 0) {
      this.eventEndingSoonNotificationTimer = setTimeout(() => {
        this.showEventEndingSoonNotification(endMoment);
      }, timeUntilNotification);
    } else {
      this.showEventEndingSoonNotification(endMoment);
    }

    // We intentionally don't apply the host check at the block level to handle edge cases
    // where users' roles change during an active event
    const timeUntilModal = timeUntilEnd - this.MODAL_TIME_THRESHOLD_SECONDS * 1000;
    if (timeUntilModal > 0) {
      this.endEventModalTimer = setTimeout(() => {
        if (this.shouldOpenEndEventModal() && this._activeEvent) {
          this.openEndEventModal(this._activeEvent._id);
        }
      }, timeUntilModal);
    } else if (this.shouldOpenEndEventModal()) {
      this.openEndEventModal(this._activeEvent._id);
    }
  }

  /**
   * Checks if the current user should be shown the end event modal.
   * This is used to handle cases where users' roles change during the event.
   */
  private shouldOpenEndEventModal(): boolean {
    return !!(
      this.spaceRepo.activeSpace &&
      Session.isOwnedByUser(this.spaceRepo.activeSpace, this.userService.user?.value?.user)
    );
  }

  private updateTimeLeft(): void {
    if (!this._activeEvent?.end_time) {
      this._timeLeft$.next(undefined);
      return;
    }

    const endMoment = moment(this._activeEvent.end_time);
    const timeLeftSeconds = Math.ceil(endMoment.diff(moment(), 'seconds', true));

    this._timeLeft$.next({
      formattedTime: this.formatTimeLeft(timeLeftSeconds),
      timeLeftSeconds,
    });
  }

  private cleanupTimers(): void {
    if (this.eventEndingSoonNotificationTimer) {
      clearTimeout(this.eventEndingSoonNotificationTimer);
    }
    if (this.endEventModalTimer) {
      clearTimeout(this.endEventModalTimer);
    }
  }

  /**
   * Formats time in seconds to HH:MM:SS or MM:SS format based on the number of hours.
   */
  private formatTimeLeft(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  // --- / Utility Methods / ---
  /**
   * Checks if the space is valid for post event action.
   */
  private isPostEventActionEnabledForSpace(space: ISession & ISpaceUI): boolean {
    return !!(space?._id && space?.institution?.settings?.postEventAction?.enablePostEventAction);
  }

  private dismissEventNotifications(): void {
    this.ngZone.run(() => {
      this.notificationToasterService.dismissNotificationsByCode([
        INFOS.EVENT_ENDING_SOON,
        INFOS.EVENT_EXTENDED,
        INFOS.EVENT_STARTED_IN_SPACE,
      ]);
    });
  }

  private showEventEndingSoonNotification(eventEndTime: moment.Moment): void {
    this.dismissEventNotifications();

    const remainingMinutes = Math.max(1, Math.round(eventEndTime.diff(moment(), 'minutes', true)));
    const notificationIcon: NotificationIconConfig = {
      assetName: 'checkered-flag-black',
      type: NotificationIconTypes.SVG_ICON,
      background: IconBackground.WARNING,
      size: 18,
      width: 18,
      height: 18,
    };

    const notificationData = new V2NotificationDataBuilder(INFOS.EVENT_ENDING_SOON)
      .type(NotificationType.WARNING)
      .title(
        `${this.translateService.instant('This event ends in')} ${remainingMinutes} ${
          remainingMinutes === 1
            ? this.translateService.instant('minute')
            : this.translateService.instant('minutes')
        }`,
      )
      .icon(notificationIcon)
      .timeOut(30)
      .build();

    this.notificationToasterService.showNotification(notificationData);
  }

  public async extendEvent(extendSeconds: number): Promise<void> {
    if (!this._activeEvent) {
      return;
    }

    try {
      await firstValueFrom(this.eventService.extendEvent(this._activeEvent._id, extendSeconds));

      this.telemetryService.logObject(
        {
          message: `Event with eventId=${this._activeEvent._id} extended by ${extendSeconds} seconds`,
        },
        this.TELEMETRY_SOURCE,
      );
    } catch (error) {
      this.telemetryService.logObject({ error }, this.TELEMETRY_SOURCE, 'error');
    }
  }

  private showEventExtendedNotification(): void {
    this.dismissEventNotifications();

    const notificationIcon: NotificationIconConfig = {
      assetName: 'alarm_add',
      type: NotificationIconTypes.MAT_ICON,
      background: IconBackground.INFO,
      size: 18,
      width: 18,
      height: 18,
    };

    const notificationData = new V2NotificationDataBuilder(INFOS.EVENT_EXTENDED)
      .type(NotificationType.INFO)
      .title(this.translateService.instant('Event extended'))
      .icon(notificationIcon)
      .timeOut(10)
      .build();

    this.ngZone.run(() => {
      this.notificationToasterService.dismissNotificationsByCode([INFOS.EVENT_EXTENDED]);
    });

    this.notificationToasterService.showNotification(notificationData);
  }
}
